import type { AIJob, OceanMissionStep } from "@vida/currents-core";
import { effectiveMissionTrigger } from "@vida/currents-core";

export type MissionIoBlock = {
  label: string;
  value: string;
};

export type ParsedMissionStep = {
  seq: number;
  kind: string;
  summary?: string | null;
  createdAt: string;
  parentSeq?: number | null;
  parallel?: boolean;
  delegateRoundId?: number | null;
  startedAt?: string | null;
  durationMs?: number | null;
  costMillicents?: number | null;
  providerUsdMicros?: number | null;
  chargedUsdMicros?: number | null;
  markup?: number | null;
  modelId?: string | null;
  agentName?: string | null;
  peerName?: string | null;
  peerId?: string | null;
  skillId?: string | null;
  toolRef?: string | null;
  exitCode?: number | null;
  errorCategory?: string | null;
  inputTokens?: number | null;
  outputTokens?: number | null;
  input: MissionIoBlock[];
  output: MissionIoBlock[];
  children?: ParsedMissionStep[];
};

/** Parse JSON once or twice (handles AWSJSON double-encoding on detailJson). */
export function safeParseJson(raw?: string | null): unknown {
  if (raw == null) return null;
  let v: unknown = raw;
  for (let i = 0; i < 2 && typeof v === "string"; i += 1) {
    try {
      v = JSON.parse(v);
    } catch {
      return null;
    }
  }
  return typeof v === "object" ? v : null;
}

function formatValue(value: unknown): string {
  if (value == null) return "";
  if (typeof value === "string") return value;
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
}

function block(label: string, value: unknown): MissionIoBlock | null {
  const text = formatValue(value).trim();
  if (!text) return null;
  return { label, value: text };
}

function blocksFromObject(obj: Record<string, unknown>, prefix = ""): MissionIoBlock[] {
  const out: MissionIoBlock[] = [];
  for (const [key, val] of Object.entries(obj)) {
    const b = block(prefix ? `${prefix}.${key}` : key, val);
    if (b) out.push(b);
  }
  return out;
}

const MODEL_LABELS: Array<[RegExp, string]> = [
  [/claude-opus-4-8/i, "Opus 4.8"],
  [/claude-opus-4-7/i, "Opus 4.7"],
  [/claude-opus-4-6/i, "Opus 4.6"],
  [/claude-opus-4-5/i, "Opus 4.5"],
  [/claude-3-opus/i, "Opus 3"],
  [/claude-sonnet-4-6/i, "Sonnet 4.6"],
  [/claude-sonnet-4-5/i, "Sonnet 4.5"],
  [/claude-3-5-sonnet/i, "Sonnet 3.5"],
  [/claude-haiku-4-5/i, "Haiku 4.5"],
  [/claude-3-5-haiku/i, "Haiku 3.5"],
  [/nova-2-lite/i, "Nova 2 Lite"],
  [/nova-pro/i, "Nova Pro"],
  [/nova-lite/i, "Nova Lite"],
  [/nova-micro/i, "Nova Micro"],
  [/gpt-image/i, "GPT Image"],
  [/dall-e/i, "DALL·E"],
];

/** Human-readable labels for mission step kinds (timeline, list). */
export const KIND_LABELS: Record<string, string> = {
  start: "Avvio",
  plan: "Piano",
  artifact: "Artifact",
  tool: "Tool",
  script: "Script",
  web_ingest: "Ingest web",
  llm_turn: "Turno LLM",
  model: "Modello",
  model_start: "Avvio modello",
  delegate: "Delega",
  delegate_start: "Delega",
  delegate_parallel: "Deleghe parallele",
  complete: "Completata",
  error: "Errore",
};

/** Short label for Bedrock modelId / inference profile in trace headers. */
export function formatModelDisplay(modelId: string): { short: string; full: string } {
  const full = (modelId ?? "").trim();
  for (const [re, label] of MODEL_LABELS) {
    if (re.test(full)) return { short: label, full };
  }
  const cleaned = full
    .replace(/^(eu\.|us\.|global\.)/, "")
    .replace(/^anthropic\.|^amazon\./, "")
    .replace(/-v\d+:\d+$/, "")
    .replace(/-\d{8}$/, "");
  return { short: cleaned || full || "(sconosciuto)", full };
}

export function formatMs(ms?: number | null): string {
  if (ms == null || !Number.isFinite(ms)) return "—";
  if (ms < 1000) return `${Math.round(ms)} ms`;
  return `${(ms / 1000).toFixed(1)} s`;
}

/** Absolute date+time (it-IT), e.g. 21/06/2026, 20:23:20 */
export function formatDateTime(iso?: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString("it-IT", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
}

/** ISO end time from step startedAt + durationMs. */
export function stepEndIso(step: { startedAt?: string | null; durationMs?: number | null }): string | null {
  if (!step.startedAt || step.durationMs == null || !Number.isFinite(step.durationMs)) return null;
  return new Date(new Date(step.startedAt).getTime() + step.durationMs).toISOString();
}

export type MissionTriggerMeta = { icon: string; label: string };

export const TRIGGER_META: Record<string, MissionTriggerMeta> = {
  USER: { icon: "👤", label: "Utente" },
  APP: { icon: "🧩", label: "App" },
  CRON: { icon: "⏰", label: "Cron" },
  WEBHOOK: { icon: "🔗", label: "Webhook" },
  API: { icon: "🔌", label: "API" },
};

export const APP_META: Record<string, { icon: string; label: string }> = {
  currents: { icon: "⚡", label: "Currents" },
  wave: { icon: "🌊", label: "Wave" },
  surf: { icon: "🏄", label: "Surf" },
  tide: { icon: "🌊", label: "Tide" },
  pulse: { icon: "📱", label: "Pulse" },
  drift: { icon: "✅", label: "Drift" },
  reef: { icon: "📰", label: "Reef" },
  vibedashboard: { icon: "🏠", label: "Vida" },
};

const USER_REQUEST_MARKER = "[Current user request]";

export function extractSubFromUserId(userId?: string | null): string | null {
  if (!userId?.trim()) return null;
  const parts = userId.split("#");
  const sub = parts.length > 0 ? parts[parts.length - 1]!.trim() : userId.trim();
  return sub || null;
}

export function extractUserQuestion(opts: {
  userQuestion?: string | null;
  goal?: string | null;
  goalPreview?: string | null;
}): string {
  if (opts.userQuestion?.trim()) return opts.userQuestion.trim();
  const goal = opts.goal?.trim() || opts.goalPreview?.trim() || "";
  const idx = goal.indexOf(USER_REQUEST_MARKER);
  if (idx >= 0) {
    const after = goal.slice(idx + USER_REQUEST_MARKER.length).trim();
    if (after) return after;
  }
  return goal;
}

export function parseContext(contextJson?: string | null): MissionIoBlock[] {
  const parsed = safeParseJson(contextJson);
  if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) return [];
  return blocksFromObject(parsed as Record<string, unknown>);
}

export type MissionOrigin = {
  appId?: string | null;
  userId?: string | null;
  userQuestion: string;
  contextJson?: string | null;
  contextBlocks: MissionIoBlock[];
  triggerType?: string | null;
  triggeredBy?: string | null;
  startedAt?: string | null;
  endedAt?: string | null;
};

export function parseMissionOriginFromJob(job: AIJob): MissionOrigin {
  const params = safeParseJson(job.paramsJson);
  const ocean =
    params && typeof params === "object" && !Array.isArray(params)
      ? (params as Record<string, unknown>).oceanAgent
      : null;
  const o =
    ocean && typeof ocean === "object" && !Array.isArray(ocean)
      ? (ocean as Record<string, unknown>)
      : null;
  const triggerType = typeof o?.triggerType === "string" ? o.triggerType.trim().toUpperCase() : null;
  const triggeredBy = typeof o?.triggeredBy === "string" ? o.triggeredBy.trim() : null;
  const goalFromParams = typeof o?.goal === "string" ? o.goal.trim() : "";
  const contextRaw =
    o?.contextJson != null
      ? typeof o.contextJson === "string"
        ? o.contextJson
        : JSON.stringify(o.contextJson)
      : null;
  const userQuestion = extractUserQuestion({
    userQuestion: typeof o?.userQuestion === "string" ? o.userQuestion : null,
    goal: job.prompt?.trim() || goalFromParams || undefined,
  });
  return {
    appId: job.appId?.trim() || "currents",
    userQuestion,
    contextJson: contextRaw,
    contextBlocks: parseContext(contextRaw),
    triggerType: triggerType || null,
    triggeredBy: triggeredBy || null,
    startedAt: job.startedAt ?? job.createdAt ?? null,
    endedAt: job.completedAt ?? null,
  };
}

export function parseHarborAssessmentLink(summary: {
  appId?: string | null;
  contextJson?: string | null;
}): { projectId: string; assessmentId: string } | null {
  if ((summary.appId || '').trim().toLowerCase() !== 'harbor') return null;
  const ctx = safeParseJson(summary.contextJson);
  if (!ctx || typeof ctx !== 'object') return null;
  const record = ctx as Record<string, unknown>;
  const projectId = String(record.projectId || '').trim();
  const assessmentId = String(record.assessmentId || '').trim();
  if (!projectId || !assessmentId) return null;
  return { projectId, assessmentId };
}

export function parseMissionOriginFromSummary(summary: {
  appId?: string | null;
  userId?: string | null;
  goalPreview?: string | null;
  userQuestion?: string | null;
  contextJson?: string | null;
  triggerType?: string | null;
  triggeredBy?: string | null;
  startedAt?: string | null;
  endedAt?: string | null;
}): MissionOrigin {
  const { triggerType, triggeredBy } = effectiveMissionTrigger(summary);
  const contextRaw = summary.contextJson?.trim() || null;
  return {
    appId: summary.appId?.trim() || "currents",
    userId: summary.userId?.trim() || null,
    userQuestion: extractUserQuestion({
      userQuestion: summary.userQuestion,
      goalPreview: summary.goalPreview,
    }),
    contextJson: contextRaw,
    contextBlocks: parseContext(contextRaw),
    triggerType,
    triggeredBy,
    startedAt: summary.startedAt ?? null,
    endedAt: summary.endedAt ?? null,
  };
}

export function resolveOriginUserSub(origin: MissionOrigin): string | null {
  const fromUserId = extractSubFromUserId(origin.userId);
  if (fromUserId) return fromUserId;
  const type = origin.triggerType?.toUpperCase();
  const by = origin.triggeredBy?.trim();
  if (!by) return null;
  if (type === "CRON" || type === "WEBHOOK" || type === "API") return null;
  const appKey = origin.appId?.trim().toLowerCase();
  if (type === "APP" && appKey && by.toLowerCase() === appKey) return null;
  return by;
}

export type ModelCostRow = {
  modelId: string;
  stepCount: number;
  providerUsdMicros: number;
  chargedUsdMicros: number;
  inputTokens: number;
  outputTokens: number;
  pricingUnmatched: boolean;
};

function flattenMissionSteps(steps: ParsedMissionStep[]): ParsedMissionStep[] {
  const out: ParsedMissionStep[] = [];
  const walk = (step: ParsedMissionStep) => {
    out.push(step);
    for (const child of step.children ?? []) walk(child);
  };
  for (const step of steps) walk(step);
  return out;
}

/** Resolve modelId per step (inherits from twin model step via skillId / toolRef). */
export function resolveStepModels(steps: ParsedMissionStep[]): Map<number, string> {
  const flat = flattenMissionSteps(steps);
  const bySkillId = new Map<string, string>();
  const byToolRef = new Map<string, string>();

  for (const step of flat) {
    if (step.kind === "model" && step.modelId) {
      if (step.skillId) bySkillId.set(step.skillId, step.modelId);
      if (step.toolRef) byToolRef.set(step.toolRef, step.modelId);
    }
  }

  const result = new Map<number, string>();
  for (const step of flat) {
    if (step.modelId?.trim()) {
      result.set(step.seq, step.modelId.trim());
      continue;
    }
    if (step.kind !== "model" && step.kind !== "model_start" && step.kind !== "llm_turn") continue;
    const inherited =
      (step.skillId && bySkillId.get(step.skillId)) ||
      (step.toolRef && byToolRef.get(step.toolRef)) ||
      null;
    if (inherited) result.set(step.seq, inherited);
  }
  return result;
}

export function resolvedModelId(
  step: ParsedMissionStep,
  stepModels?: Map<number, string>,
): string | null {
  return stepModels?.get(step.seq) ?? step.modelId?.trim() ?? null;
}

/** Aggregate llm_turn + model step costs by modelId (includes delegate children). */
export function aggregateByModel(steps: ParsedMissionStep[]): ModelCostRow[] {
  const stepModels = resolveStepModels(steps);
  const map = new Map<string, ModelCostRow>();
  for (const step of flattenMissionSteps(steps)) {
    if (step.kind !== "llm_turn" && step.kind !== "model" && step.kind !== "model_start") continue;
    const modelId = resolvedModelId(step, stepModels) || "(sconosciuto)";
    const row =
      map.get(modelId) ??
      ({
        modelId,
        stepCount: 0,
        providerUsdMicros: 0,
        chargedUsdMicros: 0,
        inputTokens: 0,
        outputTokens: 0,
        pricingUnmatched: false,
      } satisfies ModelCostRow);
    row.stepCount += 1;
    row.providerUsdMicros += step.providerUsdMicros ?? 0;
    row.chargedUsdMicros += step.chargedUsdMicros ?? 0;
    row.inputTokens += step.inputTokens ?? 0;
    row.outputTokens += step.outputTokens ?? 0;
    if (
      (step.providerUsdMicros == null || step.providerUsdMicros === 0) &&
      (step.chargedUsdMicros == null || step.chargedUsdMicros === 0) &&
      step.kind === "model"
    ) {
      row.pricingUnmatched = true;
    }
    map.set(modelId, row);
  }
  return [...map.values()].sort((a, b) => b.chargedUsdMicros - a.chargedUsdMicros);
}

/** Flat steps sorted by startedAt (fallback createdAt) for timeline. */
export function timelineSteps(steps: ParsedMissionStep[]): ParsedMissionStep[] {
  return flattenMissionSteps(steps).sort((a, b) => {
    const ta = new Date(a.startedAt ?? a.createdAt).getTime();
    const tb = new Date(b.startedAt ?? b.createdAt).getTime();
    return ta - tb;
  });
}

function readModelId(record: Record<string, unknown>): string | null {
  const raw = record.modelId ?? record.model;
  if (typeof raw !== "string") return null;
  const trimmed = raw.trim();
  return trimmed || null;
}

export function parseMissionStepIo(step: OceanMissionStep): ParsedMissionStep {
  const detail = safeParseJson(step.detailJson);
  const record =
    detail && typeof detail === "object" && !Array.isArray(detail)
      ? (detail as Record<string, unknown>)
      : {};

  const input: MissionIoBlock[] = [];
  const output: MissionIoBlock[] = [];

  switch (step.kind) {
    case "start": {
      const goal = block("goal", record.goal);
      const agentId = block("agentId", record.agentId);
      const orgId = block("orgId", record.orgId);
      if (goal) input.push(goal);
      if (agentId) input.push(agentId);
      if (orgId) input.push(orgId);
      break;
    }
    case "tool": {
      const toolName = block("tool", record.toolName);
      if (toolName) input.push(toolName);
      const toolInput = block("input", record.input);
      if (toolInput) input.push(toolInput);
      const toolOutput =
        block("output", record.output) ?? block("output", record.resultPreview);
      if (toolOutput) output.push(toolOutput);
      break;
    }
    case "web_ingest": {
      const url = block("url", (record.manifest as Record<string, unknown>)?.url ?? record.input);
      if (url) input.push(url);
      const manifest = block("manifest", record.manifest);
      if (manifest) output.push(manifest);
      break;
    }
    case "script": {
      const skill = block("skillId", record.skillId);
      const version = block("scriptVersion", record.scriptVersion);
      if (skill) input.push(skill);
      if (version) input.push(version);
      const scriptInput = block("input", record.input);
      if (scriptInput) input.push(scriptInput);
      const scriptOutput = block("output", record.output);
      if (scriptOutput) output.push(scriptOutput);
      const duration = block("durationMs", record.durationMs ?? step.durationMs);
      const exitCode = block("exitCode", record.exitCode);
      const errorCategory = block("errorCategory", record.errorCategory);
      if (duration) output.push(duration);
      if (exitCode) output.push(exitCode);
      if (errorCategory) output.push(errorCategory);
      break;
    }
    case "llm_turn": {
      const round = block("round", record.round);
      const stop = block("stopReason", record.stopReason);
      const inputRate = block("inputRatePerM", record.inputRatePerM);
      const outputRate = block("outputRatePerM", record.outputRatePerM);
      if (round) input.push(round);
      if (inputRate) input.push(inputRate);
      if (outputRate) input.push(outputRate);
      const inTok = block("inputTokens", record.inputTokens ?? step.inputTokens);
      const outTok = block("outputTokens", record.outputTokens ?? step.outputTokens);
      const cacheRead = block("cacheReadInputTokens", record.cacheReadInputTokens);
      const cacheWrite = block("cacheWriteInputTokens", record.cacheWriteInputTokens);
      if (inTok) output.push(inTok);
      if (outTok) output.push(outTok);
      if (cacheRead) output.push(cacheRead);
      if (cacheWrite) output.push(cacheWrite);
      if (stop) output.push(stop);
      break;
    }
    case "model":
    case "model_start": {
      const toolName = block("tool", record.toolName);
      const toolRefBlock = block("toolRef", record.toolRef);
      const skill = block("skillId", record.skillId);
      const model = block("modelId", record.modelId);
      const phase = block("phase", record.phase);
      const toolInput = block("input", record.input);
      if (toolName) input.push(toolName);
      if (toolRefBlock) input.push(toolRefBlock);
      if (skill) input.push(skill);
      if (model) input.push(model);
      if (phase) input.push(phase);
      const params = block("params", record.params);
      if (params) input.push(params);
      if (toolInput) input.push(toolInput);
      const prompt = block("prompt", record.prompt);
      if (prompt) output.push(prompt);
      const s3Key = block("s3Key", record.s3Key);
      const attachmentId = block("attachmentId", record.attachmentId);
      if (s3Key) output.push(s3Key);
      if (attachmentId) output.push(attachmentId);
      const duration = block("durationMs", record.durationMs);
      if (duration) output.push(duration);
      break;
    }
    case "plan": {
      const plan = block("piano", record.plan);
      const goals = block("sotto-task", record.delegateGoals);
      if (plan) input.push(plan);
      if (goals) input.push(goals);
      break;
    }
    case "delegate": {
      const peer = block("peer", record.peerName ?? record.peerId);
      const goal = block("goal", record.goal);
      if (peer) input.push(peer);
      if (goal) input.push(goal);
      const out = block("output", record.output);
      const cost = block("costCents", record.costCents);
      if (out) output.push(out);
      if (cost) output.push(cost);
      break;
    }
    case "complete": {
      const stats = blocksFromObject({
        toolCalls: record.toolCalls,
        costCents: record.costCents,
        outputChars: record.outputChars,
      });
      input.push(...stats);
      const missionOutput = block("risposta", record.output);
      if (missionOutput) output.push(missionOutput);
      break;
    }
    case "error": {
      const err = block("errore", record.message);
      if (err) output.push(err);
      break;
    }
    default: {
      if (Object.keys(record).length > 0) {
        output.push(...blocksFromObject(record));
      } else if (step.detailJson) {
        const raw = block("dettaglio", step.detailJson);
        if (raw) output.push(raw);
      }
      break;
    }
  }

  const parallel = record.parallel === true;
  const delegateRoundId =
    typeof record.delegateRoundId === "number" ? record.delegateRoundId : null;
  const modelId = readModelId(record);
  const agentName =
    step.kind === "start" && typeof record.agentName === "string"
      ? record.agentName.trim() || null
      : null;
  const isDelegateKind = step.kind === "delegate" || step.kind === "delegate_start";
  const peerName =
    isDelegateKind && typeof record.peerName === "string"
      ? record.peerName.trim() || null
      : null;
  const peerId =
    isDelegateKind && typeof record.peerId === "string" ? record.peerId.trim() || null : null;
  const skillId = typeof record.skillId === "string" ? record.skillId.trim() || null : null;
  const toolRef = typeof record.toolRef === "string" ? record.toolRef.trim() || null : null;
  const exitCode =
    step.kind === "script" && typeof record.exitCode === "number" ? record.exitCode : null;
  const errorCategory =
    step.kind === "script" && typeof record.errorCategory === "string"
      ? record.errorCategory.trim() || null
      : null;

  return {
    seq: step.seq,
    kind: step.kind,
    summary: step.summary,
    createdAt: step.createdAt,
    parentSeq: step.parentSeq ?? null,
    parallel,
    delegateRoundId,
    startedAt: step.startedAt ?? null,
    durationMs: step.durationMs ?? null,
    costMillicents: step.costMillicents ?? null,
    providerUsdMicros: step.providerUsdMicros ?? null,
    chargedUsdMicros: step.chargedUsdMicros ?? null,
    markup:
      typeof record.markup === "number"
        ? record.markup
        : step.kind === "llm_turn" && record.markup != null
          ? Number(record.markup)
          : null,
    modelId,
    agentName,
    peerName,
    peerId,
    skillId,
    toolRef,
    exitCode,
    errorCategory,
    inputTokens: step.inputTokens ?? null,
    outputTokens: step.outputTokens ?? null,
    input,
    output,
  };
}

const DELEGATE_KINDS = new Set(["delegate", "delegate_start"]);

/** Map each step seq to the agent name that executed it (orchestrator or delegated peer). */
export function resolveStepAgents(
  steps: ParsedMissionStep[],
  fallbackMissionAgent?: string | null,
): Map<number, string> {
  const flat = flattenMissionSteps(steps);
  const bySeq = new Map(flat.map((s) => [s.seq, s]));
  const startStep = flat.find((s) => s.kind === "start");
  const missionAgent =
    startStep?.agentName?.trim() || fallbackMissionAgent?.trim() || "Orchestratore";

  const result = new Map<number, string>();
  for (const step of flat) {
    let parentSeq = step.parentSeq;
    const visited = new Set<number>();
    let owner = missionAgent;

    while (parentSeq != null) {
      if (visited.has(parentSeq)) break;
      visited.add(parentSeq);
      const parent = bySeq.get(parentSeq);
      if (!parent) break;
      if (DELEGATE_KINDS.has(parent.kind) && parent.peerName?.trim()) {
        owner = parent.peerName.trim();
        break;
      }
      parentSeq = parent.parentSeq ?? null;
    }

    result.set(step.seq, owner);
  }

  return result;
}

/** Merge root-level parallel delegate steps (same delegateRoundId) into one visual group. */
export function groupParallelDelegateRoots(roots: ParsedMissionStep[]): ParsedMissionStep[] {
  const out: ParsedMissionStep[] = [];
  const consumed = new Set<number>();

  for (const step of roots) {
    if (consumed.has(step.seq)) continue;
    const roundId = step.delegateRoundId;
    const parallel = step.parallel;
    if (
      parallel &&
      roundId != null &&
      (step.kind === "delegate" || step.kind === "delegate_start")
    ) {
      const group = roots.filter(
        (s) =>
          s.parallel &&
          s.delegateRoundId === roundId &&
          (s.kind === "delegate" || s.kind === "delegate_start"),
      );
      for (const g of group) consumed.add(g.seq);
      const completes = group.filter((s) => s.kind === "delegate");
      out.push({
        seq: roundId,
        kind: "delegate_parallel",
        summary: `Deleghe parallele (${completes.length || group.length})`,
        createdAt: step.createdAt,
        parentSeq: null,
        parallel: true,
        delegateRoundId: roundId,
        input: [],
        output: [],
        children: (completes.length ? completes : group).sort((a, b) => a.seq - b.seq),
      });
      continue;
    }
    out.push(step);
  }

  return out;
}

/** Root steps with nested children (delegate sub-missions via parentSeq). */
export function buildMissionStepTree(steps: ParsedMissionStep[]): ParsedMissionStep[] {
  const bySeq = new Map<number, ParsedMissionStep>();
  for (const s of steps) {
    bySeq.set(s.seq, { ...s, children: [] });
  }
  const roots: ParsedMissionStep[] = [];
  for (const s of bySeq.values()) {
    if (s.parentSeq != null && bySeq.has(s.parentSeq)) {
      bySeq.get(s.parentSeq)!.children!.push(s);
    } else {
      roots.push(s);
    }
  }
  return roots.sort((a, b) => a.seq - b.seq);
}

export type MissionSummaryIo = {
  input: MissionIoBlock[];
  output: MissionIoBlock[];
};

export function buildMissionSummaryIo(
  job: AIJob,
  missionOutput: string | null,
): MissionSummaryIo {
  const input: MissionIoBlock[] = [];
  const output: MissionIoBlock[] = [];

  const goal = block("goal", job.prompt);
  if (goal) input.push(goal);

  const params = safeParseJson(job.paramsJson);
  if (params && typeof params === "object" && !Array.isArray(params)) {
    const ocean = (params as Record<string, unknown>).oceanAgent;
    if (ocean && typeof ocean === "object" && !Array.isArray(ocean)) {
      const o = ocean as Record<string, unknown>;
      const ctx = block("contextJson", o.contextJson);
      const role = block("role", o.role);
      const agentId = block("agentId", o.agentId);
      const orgId = block("orgId", o.orgId);
      if (role) input.push(role);
      if (agentId) input.push(agentId);
      if (orgId) input.push(orgId);
      if (ctx) input.push(ctx);
    }
  }

  const model = block("modelId", job.modelId);
  const feature = block("feature", job.feature);
  if (model) input.push(model);
  if (feature) input.push(feature);

  if (missionOutput?.trim()) {
    output.push({ label: "risposta finale", value: missionOutput.trim() });
  } else if (job.status === "COMPLETED") {
    output.push({
      label: "risposta finale",
      value: "(output non disponibile — controlla lo step complete o S3)",
    });
  }

  if (job.errorMessage) {
    output.push({ label: "errore", value: job.errorMessage });
  }

  const usage = safeParseJson(job.providerUsageJson);
  if (usage && typeof usage === "object" && !Array.isArray(usage)) {
    const u = usage as Record<string, unknown>;
    const usageBlock = block("token / tool", u.usage ?? u);
    if (usageBlock && (job.status === "COMPLETED" || job.status === "FAILED")) {
      input.push(usageBlock);
    }
  }

  return { input, output };
}
